import { registerSW } from "virtual:pwa-register";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { isPunchFlowOpen, onPunchFlowsClosed } from "./cleaner/flowState";
import { startAutoFlush } from "./lib/offlineQueue";
import "./styles.css";
// NKLEAN "modern app" theme. Loaded last so it refines the base styles and the cleaner/admin styles.
import "./theme.css";

// --- App updates -------------------------------------------------------------
//
// A new version is applied (and the page reloaded) only when no clock-in/out flow is open:
// right away if it was already waiting when the app started, otherwise the next time the
// app goes into the background. Never in the middle of scanning, locating or sending.

let updateWaiting = false;
let reloadWanted = false;

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    updateWaiting = true;
    // Mostly this fires while the app is in use (the new version finished downloading):
    // wait for the background, unless the app isn't being looked at anyway.
    if (document.visibilityState === "hidden") applyUpdate();
  },
  onNeedReload() {
    // The new version has taken over: reload into it, but not while a flow is open.
    reloadWanted = true;
    reloadIfSafe();
  },
  onRegisteredSW(_url, registration) {
    // Already waiting at start-up (downloaded last time): nothing is open yet, so switch now.
    if (registration?.waiting) {
      updateWaiting = true;
      applyUpdate();
    }
  },
});

function applyUpdate() {
  if (!updateWaiting || isPunchFlowOpen()) return;
  updateWaiting = false;
  void updateSW(true);
}

function reloadIfSafe() {
  if (reloadWanted && !isPunchFlowOpen()) window.location.reload();
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") applyUpdate();
});
onPunchFlowsClosed(() => {
  reloadIfSafe();
  if (document.visibilityState === "hidden") applyUpdate();
});

// Send any clock-ins/outs queued while offline, on every page (not just the clock screen).
startAutoFlush();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
